import { Button, Form, Input } from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";
import { cleanEmptyStrings } from "@shared/hooks";

import type { AdminLicenseAssignmentFilterData } from "../../schemas";

interface AdminLicenseAssignmentsFilterProps {
  onFilter: (data: Partial<AdminLicenseAssignmentFilterData>) => void;
  onClear: () => void;
  initialFilters?: Partial<AdminLicenseAssignmentFilterData>;
}

const EMPTY_FORM: AdminLicenseAssignmentFilterData = {
  complex: "",
  structure: "",
  module: "",
  user: "",
};

export const AdminLicenseAssignmentsFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: AdminLicenseAssignmentsFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<AdminLicenseAssignmentFilterData>({
    defaultValues: {
      complex: initialFilters?.complex ?? "",
      structure: initialFilters?.structure ?? "",
      module: initialFilters?.module ?? "",
      user: initialFilters?.user ?? "",
    },
  });

  const handleFilter = (data: AdminLicenseAssignmentFilterData) => {
    onFilter(
      cleanEmptyStrings(data) as Partial<AdminLicenseAssignmentFilterData>,
    );
  };

  const handleClear = () => {
    form.reset(EMPTY_FORM);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <Input
            control={form.control}
            label={`${translate("admin.licenseAssignments.filter.label.complex")}:`}
            name="complex"
            type="text"
            placeholder={translate(
              "admin.licenseAssignments.filter.placeholder.complex",
            )}
          />
          <Input
            control={form.control}
            label={`${translate("admin.licenseAssignments.filter.label.structure")}:`}
            name="structure"
            type="text"
            placeholder={translate(
              "admin.licenseAssignments.filter.placeholder.structure",
            )}
          />
          <Input
            control={form.control}
            label={`${translate("admin.licenseAssignments.filter.label.module")}:`}
            name="module"
            type="text"
            placeholder={translate(
              "admin.licenseAssignments.filter.placeholder.module",
            )}
          />
          <Input
            control={form.control}
            label={`${translate("admin.licenseAssignments.filter.label.user")}:`}
            name="user"
            type="text"
            placeholder={translate(
              "admin.licenseAssignments.filter.placeholder.user",
            )}
          />
        </div>
        <div className="flex justify-end mb-4 gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate("admin.licenseAssignments.filter.button.clearFilters")}
          </Button>
          <Button type="submit" iconLeft={<Filter fill="currentColor" />}>
            {translate("admin.licenseAssignments.filter.button.filter")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
